/**
 * Authentication API functions
 */
import { LoginRequest, RegisterRequest, TokenResponse, UserMe } from '@/types';
import { apiClient } from './client';

export const authApi = {
  /**
   * Login user
   */
  async login(credentials: LoginRequest): Promise<TokenResponse> {
    // Cookies are automatically sent by browser with withCredentials: true
    return apiClient.post<TokenResponse>('/auth/login', credentials);
  },

  /**
   * Register new user; response matches login (tokens + server sets httpOnly cookies).
   */
  async register(userData: RegisterRequest): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>('/auth/register', userData);
  },

  /**
   * Refresh access token
   */
  async refreshToken(refreshToken: string): Promise<TokenResponse> {
    return apiClient.post<TokenResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
  },

  /**
   * Get current user info
   */
  async getCurrentUser(): Promise<UserMe> {
    return apiClient.get<UserMe>('/users/me');
  },

  /**
   * Logout (optional - mainly for logging purposes)
   */
  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors - we'll clear local storage anyway
      console.warn('Logout API call failed:', error);
    }
  },

  /**
   * Change password
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },

  /**
   * Request password reset
   */
  async requestPasswordReset(email: string): Promise<void> {
    return apiClient.post('/auth/password-reset/request', { email });
  },

  /**
   * Reset password with token
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    return apiClient.post('/auth/password-reset/confirm', {
      token,
      new_password: newPassword,
    });
  },

  /**
   * Verify email with token
   */
  async verifyEmail(token: string): Promise<void> {
    return apiClient.post('/auth/verify-email', { token });
  },

  /**
   * Resend verification email
   */
  async resendVerificationEmail(): Promise<void> {
    return apiClient.post('/auth/resend-verification');
  },
};
